import { FC, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, Trash2, X } from 'lucide-react';
import * as Sentry from '@sentry/react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { useNotification } from '@/components/notifications';
import { useRemoveContextGraphItem } from '@/hooks/mutations/context-graphs/use-remove-context-graph-item';
import { queryContextGraph } from '@/services/context-graphs/query-context-graph';
import {
  GraphSelection,
  formatValidAt,
  linkEndpoints,
} from '@/components/context-graph-viewer/helpers/graph-types';

interface DetailCardProps {
  selection: GraphSelection;
  typeColors: Map<string, string>;
  graphId: string;
  canEdit?: boolean;
  onClose: () => void;
  onConfirmOpenChange?: (open: boolean) => void;
  onRemoved: (selection: GraphSelection) => void;
}

export const DetailCard: FC<DetailCardProps> = ({
  selection,
  typeColors,
  graphId,
  canEdit = false,
  onClose,
  onConfirmOpenChange,
  onRemoved,
}) => {
  const notification = useNotification();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // The card remounts per selection (keyed by node/link id in ForceGraph),
  // so mutation state never leaks across selections.
  const explain = useMutation({
    mutationFn: () => {
      if (selection.kind !== 'node') {
        return Promise.resolve([]);
      }
      return queryContextGraph(
        graphId,
        `What is known about "${selection.node.label}"? Summarize the key relationships.`,
      );
    },
    onError: (error) => {
      Sentry.captureException(error, {
        extra: {
          graphId,
          node: selection.kind === 'node' ? selection.node.label : undefined,
        },
      });
    },
  });

  const removeItem = useRemoveContextGraphItem();

  const updateConfirmOpen = (open: boolean) => {
    setConfirmOpen(open);
    onConfirmOpenChange?.(open);
  };

  // The card can unmount with the dialog open (successful removal);
  // make sure the parent doesn't stay locked in dialog-open mode.
  useEffect(() => {
    return () => onConfirmOpenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExplain = () => {
    if (selection.kind !== 'node') return;
    explain.mutate();
  };

  const removing = removeItem.isPending;

  const handleRemove = () => {
    removeItem.mutate(
      selection.kind === 'node'
        ? { graphId, kind: 'entity', id: selection.node.id }
        : { graphId, kind: 'fact', id: selection.link.id },
      {
        onSuccess: () => {
          notification.success(
            selection.kind === 'node'
              ? 'Entity removed from graph'
              : 'Fact removed from graph',
          );
          updateConfirmOpen(false);
          onRemoved(selection);
        },
        onError: () => {
          notification.error(
            selection.kind === 'node'
              ? 'Failed to remove entity'
              : 'Failed to remove fact',
          );
        },
      },
    );
  };

  const selectedValidAt =
    selection.kind === 'edge' ? formatValidAt(selection.link.validAt) : null;

  return (
    <div className='bg-background/95 absolute top-3 right-3 flex max-h-[70%] w-72 flex-col rounded-lg border p-4 shadow-lg backdrop-blur-sm'>
      <div className='flex items-start justify-between gap-2'>
        {selection.kind === 'node' ? (
          <div className='flex items-center gap-2'>
            <span
              className='size-3 shrink-0 rounded-full'
              style={{ backgroundColor: typeColors.get(selection.node.type) }}
            />
            <span className='text-sm leading-tight font-semibold'>
              {selection.node.label}
            </span>
          </div>
        ) : (
          <Badge variant='secondary' className='font-mono text-[10px]'>
            {selection.link.label || 'RELATES_TO'}
          </Badge>
        )}
        <button
          type='button'
          onClick={onClose}
          className='text-muted-foreground hover:text-foreground shrink-0'
          aria-label='Close details'
        >
          <X className='size-4' />
        </button>
      </div>
      <div className='flex flex-col gap-2 overflow-y-auto pt-2'>
        {selection.kind === 'node' ? (
          <>
            <div className='flex items-center gap-2'>
              <Badge variant='outline' className='text-[10px]'>
                {selection.node.type}
              </Badge>
              <span className='text-muted-foreground text-xs'>
                {selection.node.degree}{' '}
                {selection.node.degree === 1 ? 'connection' : 'connections'}
              </span>
            </div>
            {selection.node.summary ? (
              <p className='text-muted-foreground line-clamp-6 text-xs leading-relaxed'>
                {selection.node.summary}
              </p>
            ) : null}
            {explain.status === 'idle' ? (
              <Button
                variant='outline'
                size='sm'
                className='mt-1 h-7 self-start text-xs'
                onClick={handleExplain}
              >
                <Sparkles className='mr-1.5 size-3.5' />
                Explain
              </Button>
            ) : null}
            {explain.status === 'pending' ? (
              <p className='text-muted-foreground animate-pulse text-xs'>
                Asking the graph…
              </p>
            ) : null}
            {explain.status === 'error' ? (
              <p className='text-muted-foreground text-xs'>
                Couldn&apos;t query the graph.{' '}
                <button
                  type='button'
                  className='underline underline-offset-2'
                  onClick={handleExplain}
                >
                  Retry
                </button>
              </p>
            ) : null}
            {explain.status === 'success' ? (
              <div className='border-t pt-2'>
                <p className='text-muted-foreground mb-1 text-[10px] font-medium tracking-wide uppercase'>
                  From the graph
                </p>
                {(explain.data ?? []).length === 0 ? (
                  <p className='text-muted-foreground text-xs'>
                    No related facts found.
                  </p>
                ) : (
                  <ul className='flex flex-col gap-1.5'>
                    {(explain.data ?? []).map((fact, index) => (
                      <li
                        key={index}
                        className='text-xs leading-relaxed before:mr-1.5 before:content-["·"]'
                      >
                        {fact.fact}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <p className='text-xs leading-relaxed'>{selection.link.fact}</p>
            <p className='text-muted-foreground text-xs'>
              {linkEndpoints(selection.link).source.label} →{' '}
              {linkEndpoints(selection.link).target.label}
            </p>
            {selectedValidAt ? (
              <p className='text-muted-foreground/70 text-[11px]'>
                Since {selectedValidAt}
              </p>
            ) : null}
          </>
        )}
        {canEdit ? (
          <div className='mt-1 border-t pt-2'>
            <Button
              variant='outline'
              size='sm'
              className='h-7 self-start text-xs'
              onClick={() => updateConfirmOpen(true)}
            >
              <Trash2 className='mr-1.5 size-3.5' />
              Remove from graph
            </Button>
          </div>
        ) : null}
      </div>

      <Modal
        title={
          selection.kind === 'node'
            ? `Remove ${selection.node.label}?`
            : 'Remove this fact?'
        }
        description={
          selection.kind === 'node'
            ? 'This permanently deletes the entity and all of its facts from the graph.'
            : 'This permanently deletes the fact from the graph. The entities it connects are kept.'
        }
        isDialogOpen={confirmOpen}
        setIsDialogOpen={updateConfirmOpen}
        footer={
          <>
            <Button
              variant='outline'
              onClick={() => updateConfirmOpen(false)}
              disabled={removing}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleRemove}
              disabled={removing}
            >
              {removing ? 'Removing…' : 'Remove'}
            </Button>
          </>
        }
      />
    </div>
  );
};
